import { createClient } from './chatClient';
import { createChatLogger, createRecordingCoordinator } from './logger';
import { normalizeMessages } from './utils/normalizeMessages';
import { collectCitationTitles } from './utils/collectCitationTitles';
import { resolveCiteMarkersInActiveTools } from './utils/activeToolCitations';

/** @internal For testing only — not part of the public API. */

export function createChatManager(options) {
  const optionsRef = {
    current: options
  };
  const clients = new Map();
  const unsubscribers = new Map();
  const msgCache = new Map();
  const listeners = new Set();
  let coordinator;
  try {
    if (localStorage.getItem('breeze-engine:record') === 'true') {
      coordinator = createRecordingCoordinator();
    }
  } catch (_unused) {
    // storage unavailable — coordinator stays undefined
  }
  let activeConversationId = null;
  let cachedSnapshot = null;
  function notify() {
    cachedSnapshot = null;
    for (const listener of listeners) {
      listener();
    }
  }
  function destroyEntry(key) {
    var _unsubscribers$get, _clients$get, _coordinator;
    (_unsubscribers$get = unsubscribers.get(key)) === null || _unsubscribers$get === void 0 || _unsubscribers$get();
    (_clients$get = clients.get(key)) === null || _clients$get === void 0 || _clients$get.destroy();
    clients.delete(key);
    unsubscribers.delete(key);
    msgCache.delete(key);
    (_coordinator = coordinator) === null || _coordinator === void 0 || _coordinator.unregister(key);
  }
  function createClientEntry(initialKey, loadId) {
    var _optionsRef$current$_;
    if (initialKey === null && clients.has(null)) {
      const existing = clients.get(null);
      const snap = existing.getSnapshot();
      if (snap.status !== 'READY' && snap.status !== 'ERROR') {
        // Don't destroy a null-key client that is actively loading/streaming.
        // This guards against stale-snapshot callers (e.g. ensureNew + openPanel
        // in the same tick) accidentally killing an in-flight request.
        return;
      }
      destroyEntry(null);
    }
    let currentKey = initialKey;
    const clientFactory = (_optionsRef$current$_ = optionsRef.current.__createClient) !== null && _optionsRef$current$_ !== void 0 ? _optionsRef$current$_ : createClient;
    const logger = createChatLogger({
      coordinator,
      initialKey
    });
    const client = clientFactory(Object.assign({
      agentId: optionsRef.current.agentId,
      mode: optionsRef.current.mode,
      tools: optionsRef.current.tools,
      logger,
      chirpClient: optionsRef.current.chirpClient,
      pollingChirpClient: optionsRef.current.pollingChirpClient,
      conversationsChirpClient: optionsRef.current.conversationsChirpClient,
      pollConversationTitle: optionsRef.current.pollConversationTitle
    }, optionsRef.current.mode === 'ably' && optionsRef.current.ablyClient ? {
      ablyClient: optionsRef.current.ablyClient
    } : {}, optionsRef.current.mode === 'ably' ? {
      getAblyAuthToken: conversationId => {
        const current = optionsRef.current;
        if (current.mode === 'ably' && current.getAblyAuthToken) {
          return current.getAblyAuthToken(conversationId);
        }
        return null;
      }
    } : {}, {
      onConversationCreated: newId => {
        var _optionsRef$current$o, _optionsRef$current;
        const existingClient = clients.get(currentKey);
        const existingUnsub = unsubscribers.get(currentKey);
        const existingMsgs = msgCache.get(currentKey);
        clients.delete(currentKey);
        unsubscribers.delete(currentKey);
        msgCache.delete(currentKey);
        const prevKey = currentKey;
        currentKey = newId;
        if (existingClient) clients.set(newId, existingClient);
        if (existingUnsub) unsubscribers.set(newId, existingUnsub);
        if (existingMsgs) msgCache.set(newId, existingMsgs);
        if (activeConversationId === prevKey) {
          var _coordinator2;
          activeConversationId = newId;
          (_coordinator2 = coordinator) === null || _coordinator2 === void 0 || _coordinator2.setActive(newId);
        }
        notify();
        (_optionsRef$current$o = (_optionsRef$current = optionsRef.current).onConversationCreated) === null || _optionsRef$current$o === void 0 || _optionsRef$current$o.call(_optionsRef$current, newId);
      },
      onStatusChange: (status, prevStatus) => {
        var _clients$get2;
        if (!optionsRef.current.onStatusChange) return;
        optionsRef.current.onStatusChange(currentKey, status, prevStatus, (_clients$get2 = clients.get(currentKey)) === null || _clients$get2 === void 0 ? void 0 : _clients$get2.getSnapshot().activeTools);
      },
      onStreamEvent: event => {
        var _optionsRef$current$o2, _optionsRef$current2;
        (_optionsRef$current$o2 = (_optionsRef$current2 = optionsRef.current).onStreamEvent) === null || _optionsRef$current$o2 === void 0 || _optionsRef$current$o2.call(_optionsRef$current2, currentKey, event);
      },
      onError: error => {
        var _optionsRef$current$o3, _optionsRef$current3;
        (_optionsRef$current$o3 = (_optionsRef$current3 = optionsRef.current).onError) === null || _optionsRef$current$o3 === void 0 || _optionsRef$current$o3.call(_optionsRef$current3, currentKey, error);
      },
      onTitleChanged: (cId, title) => {
        var _optionsRef$current$o4, _optionsRef$current4;
        (_optionsRef$current$o4 = (_optionsRef$current4 = optionsRef.current).onTitleChanged) === null || _optionsRef$current$o4 === void 0 || _optionsRef$current$o4.call(_optionsRef$current4, cId, title);
      }
    }));
    const unsubscribe = client.subscribe(() => {
      notify();
    });
    if (optionsRef.current.externalSkillSpecs) {
      client.setExternalSkillSpecs(optionsRef.current.externalSkillSpecs);
    }
    if (optionsRef.current.externalSubagentSpecs) {
      client.setExternalSubagentSpecs(optionsRef.current.externalSubagentSpecs);
    }
    clients.set(currentKey, client);
    unsubscribers.set(currentKey, unsubscribe);
    if (loadId !== undefined) {
      void client.loadConversation(loadId);
    }
  }
  function getSnapshot() {
    var _conversations$get;
    if (cachedSnapshot !== null) return cachedSnapshot;
    const conversations = new Map();
    for (const [key, client] of clients) {
      const snap = client.getSnapshot();
      const cached = msgCache.get(key);
      let normalized;
      let citationTitles;
      if (cached && cached.raw === snap.messages && cached.rawTurnIds === snap.messageTurnIds && cached.rawFeedback === snap.feedbackByTurnId && cached.rawCitationFeedback === snap.feedbackByCitationKey) {
        normalized = cached.normalized;
        citationTitles = cached.citationTitles;
      } else {
        normalized = normalizeMessages(snap.messages, snap.messageTurnIds, optionsRef.current.resolveCitationUrl, snap.feedbackByTurnId, snap.feedbackByCitationKey);
        citationTitles = collectCitationTitles(snap.messages, optionsRef.current.resolveCitationUrl);
      }

      // Cache the resolved activeTools keyed on the raw activeTools + titles
      // identity so consumers (useChatManager) keep a stable reference across
      // snapshot ticks triggered by unrelated state changes.
      let resolvedActiveTools;
      if (cached && cached.rawActiveTools === snap.activeTools && cached.citationTitles === citationTitles) {
        resolvedActiveTools = cached.resolvedActiveTools;
      } else {
        resolvedActiveTools = resolveCiteMarkersInActiveTools(snap.activeTools, citationTitles);
      }
      msgCache.set(key, {
        raw: snap.messages,
        rawTurnIds: snap.messageTurnIds,
        rawFeedback: snap.feedbackByTurnId,
        rawCitationFeedback: snap.feedbackByCitationKey,
        normalized,
        citationTitles,
        rawActiveTools: snap.activeTools,
        resolvedActiveTools
      });
      conversations.set(key, {
        conversationId: snap.conversationId,
        messages: normalized,
        status: snap.status,
        error: snap.error,
        turnId: snap.turnId,
        title: snap.title,
        agentId: snap.agentId,
        activeTools: resolvedActiveTools,
        queuedMessages: snap.queuedMessages,
        history: snap.history
      });
    }
    const active = (_conversations$get = conversations.get(activeConversationId)) !== null && _conversations$get !== void 0 ? _conversations$get : null;
    cachedSnapshot = {
      conversations,
      activeConversationId,
      active
    };
    return cachedSnapshot;
  }
  function getActiveClient() {
    return clients.get(activeConversationId);
  }
  const manager = {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot,
    open(conversationId) {
      var _coordinator4;
      if (clients.has(conversationId)) {
        var _coordinator3;
        activeConversationId = conversationId;
        (_coordinator3 = coordinator) === null || _coordinator3 === void 0 || _coordinator3.setActive(conversationId);
        notify();
        return;
      }
      createClientEntry(conversationId, conversationId);
      activeConversationId = conversationId;
      (_coordinator4 = coordinator) === null || _coordinator4 === void 0 || _coordinator4.setActive(conversationId);
      notify();
    },
    startNew() {
      var _coordinator5;
      createClientEntry(null);
      activeConversationId = null;
      (_coordinator5 = coordinator) === null || _coordinator5 === void 0 || _coordinator5.setActive(null);
      notify();
    },
    close(conversationId) {
      if (!clients.has(conversationId)) return;
      destroyEntry(conversationId);
      if (activeConversationId === conversationId) {
        var _coordinator6;
        activeConversationId = null;
        (_coordinator6 = coordinator) === null || _coordinator6 === void 0 || _coordinator6.setActive(null);
      }
      notify();
    },
    setActive(conversationId) {
      var _coordinator7;
      activeConversationId = conversationId;
      (_coordinator7 = coordinator) === null || _coordinator7 === void 0 || _coordinator7.setActive(conversationId);
      notify();
    },
    sendMessage(content, sendOptions) {
      var _getActiveClient$send, _getActiveClient;
      if (!clients.has(activeConversationId)) {
        var _coordinator8;
        createClientEntry(null);
        activeConversationId = null;
        (_coordinator8 = coordinator) === null || _coordinator8 === void 0 || _coordinator8.setActive(null);
        notify();
      }
      return (_getActiveClient$send = (_getActiveClient = getActiveClient()) === null || _getActiveClient === void 0 ? void 0 : _getActiveClient.sendMessage(content, sendOptions)) !== null && _getActiveClient$send !== void 0 ? _getActiveClient$send : Promise.resolve();
    },
    stop() {
      var _getActiveClient$stop, _getActiveClient2;
      return (_getActiveClient$stop = (_getActiveClient2 = getActiveClient()) === null || _getActiveClient2 === void 0 ? void 0 : _getActiveClient2.stop()) !== null && _getActiveClient$stop !== void 0 ? _getActiveClient$stop : Promise.resolve();
    },
    clear() {
      var _getActiveClient3;
      (_getActiveClient3 = getActiveClient()) === null || _getActiveClient3 === void 0 || _getActiveClient3.clear();
    },
    submitFeedback(feedbackOptions) {
      var _getActiveClient$subm, _getActiveClient4;
      return (_getActiveClient$subm = (_getActiveClient4 = getActiveClient()) === null || _getActiveClient4 === void 0 ? void 0 : _getActiveClient4.submitFeedback(feedbackOptions)) !== null && _getActiveClient$subm !== void 0 ? _getActiveClient$subm : Promise.resolve();
    },
    addToolResult(toolUseId, result) {
      var _getActiveClient5;
      (_getActiveClient5 = getActiveClient()) === null || _getActiveClient5 === void 0 || _getActiveClient5.addToolResult(toolUseId, result);
    },
    approveTool(toolUseId, result) {
      var _getActiveClient6;
      (_getActiveClient6 = getActiveClient()) === null || _getActiveClient6 === void 0 || _getActiveClient6.approveTool(toolUseId, result);
    },
    removeQueuedMessage(id) {
      var _getActiveClient7;
      (_getActiveClient7 = getActiveClient()) === null || _getActiveClient7 === void 0 || _getActiveClient7.removeQueuedMessage(id);
    },
    resumeTurn(turnId, lastMessageId) {
      var _getActiveClient$resu, _getActiveClient8;
      return (_getActiveClient$resu = (_getActiveClient8 = getActiveClient()) === null || _getActiveClient8 === void 0 ? void 0 : _getActiveClient8.resumeTurn(turnId, lastMessageId)) !== null && _getActiveClient$resu !== void 0 ? _getActiveClient$resu : Promise.resolve();
    },
    loadMore() {
      var _getActiveClient9;
      (_getActiveClient9 = getActiveClient()) === null || _getActiveClient9 === void 0 || _getActiveClient9.loadMore();
    },
    injectMessage(message) {
      var _getActiveClient10;
      (_getActiveClient10 = getActiveClient()) === null || _getActiveClient10 === void 0 || _getActiveClient10.injectMessage(message);
    },
    setAgentId(agentId) {
      var _coordinator9;
      if (agentId === optionsRef.current.agentId) return;
      optionsRef.current = Object.assign({}, optionsRef.current, {
        agentId
      });
      for (const key of [...clients.keys()]) {
        destroyEntry(key);
      }
      activeConversationId = null;
      (_coordinator9 = coordinator) === null || _coordinator9 === void 0 || _coordinator9.setActive(null);
      notify();
    },
    setAblyAuthToken(fn) {
      if (optionsRef.current.mode !== 'ably') return;
      if (fn === optionsRef.current.getAblyAuthToken) return;
      optionsRef.current = Object.assign({}, optionsRef.current, {
        getAblyAuthToken: fn
      });
    },
    setTools(tools) {
      optionsRef.current = Object.assign({}, optionsRef.current, {
        tools
      });
      for (const client of clients.values()) {
        client.setTools(tools);
      }
    },
    setExternalSkillSpecs(specs) {
      optionsRef.current = Object.assign({}, optionsRef.current, {
        externalSkillSpecs: specs
      });
      for (const client of clients.values()) {
        client.setExternalSkillSpecs(specs);
      }
    },
    setExternalSubagentSpecs(specs) {
      optionsRef.current = Object.assign({}, optionsRef.current, {
        externalSubagentSpecs: specs
      });
      for (const client of clients.values()) {
        client.setExternalSubagentSpecs(specs);
      }
    },
    destroy() {
      var _coordinator10;
      for (const key of [...clients.keys()]) {
        destroyEntry(key);
      }
      (_coordinator10 = coordinator) === null || _coordinator10 === void 0 || _coordinator10.setActive(null);
      listeners.clear();
      cachedSnapshot = null;
    }
  };
  return manager;
}